import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin, ChevronRight, Star, ExternalLink, Send } from 'lucide-react';
import { cmsAPI, settingsAPI, analyticsAPI } from '../../lib/api';

// Logo MSC
const MSC_LOGO_URL = "https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/msqh8r9l_Logo-MSC-site-BLANC.png";

const Footer = () => {
  const [footerData, setFooterData] = useState(null);
  const [settings, setSettings] = useState({});
  const [build, setBuild] = useState({ version: '', started_at: '', git_commit: '' });

  useEffect(() => {
    loadFooterData();
    // Récupère la version + horodatage de déploiement directement depuis le backend.
    // started_at = moment où le serveur a redémarré (= heure du dernier déploiement Emergent).
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/version`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setBuild(d); })
      .catch(() => {});
  }, []);

  const loadFooterData = async () => {
    try {
      const [sectionsRes, settingsRes] = await Promise.all([
        cmsAPI.getSections('global'),
        settingsAPI.get()
      ]);
      const footer = sectionsRes.data.find(s => s.section_key === 'footer');
      setFooterData(footer);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error loading footer data:', error);
    }
  };

  // Version + dernière mise à jour : viennent directement du backend (heure de démarrage = heure de déploiement).
  // Fallback sur settings si /api/version indisponible.
  const appVersion = build.version || settings.app_version || "1.6.0";
  const deployedAt = build.started_at
    ? new Date(build.started_at).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
        timeZone: 'Europe/Paris'
      })
    : (settings.last_update_date || "26 avril 2026");

  const logoUrl = settings.logo_url || footerData?.image_url || 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/9i39bysf_475444528_1384443796015613_5677894820733752221_n%20%281%29.jpg';
  
  // Logo pour fond sombre
  const logoDarkUrl = 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/o5ssfxmc_english%20hero.png';

  const quickLinks = footerData?.links?.slice(0, 6) || [
    { name: 'Accueil', path: '/' },
    { name: 'Programmes Enfants', path: '/programmes/enfants' },
    { name: 'Programmes Adultes', path: '/programmes/adultes' },
    { name: 'Certifications Cambridge', path: '/cambridge' },
    { name: 'Demande d\'infos', path: '/demande-info' },
    { name: 'Contact', path: '/contact' },
  ];

  const legalLinks = [
    { name: 'Mentions légales', path: '/mentions-legales' },
    { name: 'CGV', path: '/cgv' },
    { name: 'Règlement intérieur', path: '/reglement' },
    { name: 'Politique de confidentialité', path: '/politique-confidentialite' },
  ];
  
  // URLs des certifications depuis les settings (avec fallbacks codés en dur pour que les liens
  // fonctionnent toujours en prod même si la BDD de prod n'a pas encore les URLs renseignées).
  const QUALIOPI_DEFAULT_URL = "https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/h8qyl496_certificat-B03850-2025-03-09T23_00_00Z_0JSQWNJ8PS8KH.pdf";
  const CAMBRIDGE_DEFAULT_URL = "https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/omxybuld_English%20Speaking%20Center%20%281%29.pdf";
  const qualiopiCertificateUrl = settings.qualiopi_certificate_url || QUALIOPI_DEFAULT_URL;
  const qualiopiLogoUrl = settings.qualiopi_logo_url || "/qualiopi-logo.png";
  const cambridgeCertificateUrl = settings.cambridge_certificate_url || CAMBRIDGE_DEFAULT_URL;
  
  // Liens vers les avis Google et Facebook
  const googleReviewsUrl = settings.google_reviews_url || "https://share.google/Xdci0bGmTO7xijiji";
  const facebookReviewsUrl = settings.facebook_reviews_url || "https://www.facebook.com/share/1Nmak4hrkt/";
  const leveltelLogoUrl = "https://customer-assets.emergentagent.com/job_8ac9ff11-8439-4edd-b71a-13f35871b045/artifacts/5sovhi7v_LEVELTEL_LOGO.jpg";

  return (
    <footer className="bg-gradient-to-b from-[#2C3C65] to-[#1a2640]" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-6">
              <img 
                src={logoDarkUrl} 
                alt="The English Speaking Center" 
                className="h-20 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              {footerData?.content || "Centre de formation en anglais à Istres. Préparation aux certifications Cambridge English pour tous les âges. Centre agréé Qualiopi, éligible CPF."}
            </p>
            <div className="flex space-x-4">
              {settings.facebook_url && (
                <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-gray-300 hover:bg-[#285BBB] hover:text-white transition-all">
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {settings.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-gray-300 hover:bg-[#285BBB] hover:text-white transition-all">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {settings.linkedin_url && (
                <a href={settings.linkedin_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-gray-300 hover:bg-[#285BBB] hover:text-white transition-all">
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              {!settings.facebook_url && !settings.instagram_url && !settings.linkedin_url && (
                <>
                  <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-gray-300 hover:bg-[#285BBB] hover:text-white transition-all">
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-gray-300 hover:bg-[#285BBB] hover:text-white transition-all">
                    <Instagram className="h-5 w-5" />
                  </a>
                  <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-gray-300 hover:bg-[#285BBB] hover:text-white transition-all">
                    <Linkedin className="h-5 w-5" />
                  </a>
                </>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white text-lg font-bold mb-6 relative">
              Liens rapides
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-[#ff3a2d] -mb-2"></span>
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, idx) => (
                <li key={idx}>
                  <Link 
                    to={link.path} 
                    className="text-gray-300 hover:text-white hover:pl-2 transition-all flex items-center group"
                  >
                    <ChevronRight className="h-4 w-4 mr-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white text-lg font-bold mb-6 relative">
              Contact
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-[#ff3a2d] -mb-2"></span>
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-[#285BBB]/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-[#285BBB]" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Adresse</p>
                  <p className="text-gray-300 text-sm">
                    {settings.address || '9 Allée Jean Jaurès'}<br />
                    {!settings.address && '13800 Istres'}
                  </p>
                  {/* Notice PMR discrète */}
                  <p className="text-gray-500 text-xs mt-2 italic">
                    Accessibilité PMR : contactez-nous pour trouver une solution adaptée.
                  </p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-[#285BBB]/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-[#285BBB]" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Email</p>
                  <a 
                    href="mailto:contact@theenglishspeakingcenter.fr?cc=theenglishspeakingcenter@gmail.com"
                    onClick={() => analyticsAPI.trackClick('email').catch(() => {})}
                    className="inline-flex items-center gap-2 mt-1 px-4 py-2 bg-[#285BBB] text-white text-sm font-medium rounded-lg hover:bg-[#1e4a9a] transition-colors"
                  >
                    <Send className="h-4 w-4" />
                    Envoyer un email
                  </a>
                </div>
              </li>
              {settings.contact_phone && (
                <li className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-[#285BBB]/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-[#285BBB]" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Téléphone</p>
                    <a
                      href={`tel:${settings.contact_phone}`}
                      onClick={() => analyticsAPI.trackClick('phone').catch(() => {})}
                      className="text-gray-300 hover:text-[#285BBB] text-sm transition-colors"
                    >
                      {settings.contact_phone}
                    </a>
                  </div>
                </li>
              )}
            </ul>
          </div>

          {/* Certifications */}
          <div>
            <h3 className="text-white text-lg font-bold mb-6 relative">
              Certifications
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-[#ff3a2d] -mb-2"></span>
            </h3>
            <div className="space-y-4">
              {/* Logo Qualiopi cliquable pour télécharger le certificat */}
              {qualiopiCertificateUrl ? (
                <a 
                  href={qualiopiCertificateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white rounded-lg p-4 hover:shadow-lg transition-all group"
                  title="Télécharger le certificat Qualiopi"
                >
                  <img 
                    src="/qualiopi-new.png" 
                    alt="Qualiopi" 
                    className="h-14 w-auto mx-auto"
                  />
                  <p className="text-center text-gray-500 text-xs mt-2 flex items-center justify-center group-hover:text-[#285BBB] transition-colors">
                    Certifié Qualiopi
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </p>
                </a>
              ) : (
                <div className="block bg-white rounded-lg p-4">
                  <img 
                    src="/qualiopi-new.png" 
                    alt="Qualiopi" 
                    className="h-14 w-auto mx-auto"
                  />
                  <p className="text-center text-gray-500 text-xs mt-2">
                    Certifié Qualiopi
                  </p>
                </div>
              )}
              
              {/* Logo Cambridge Assessment - Cliquable vers certificat */}
              {cambridgeCertificateUrl ? (
                <a 
                  href={cambridgeCertificateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white rounded-lg p-4 hover:shadow-lg transition-all group"
                  title="Télécharger le certificat Cambridge"
                  data-testid="footer-cambridge-certificate-link"
                >
                  <img 
                    src="/cambridge-assessment.png" 
                    alt="Cambridge Assessment" 
                    className="h-14 w-auto mx-auto"
                  />
                  <p className="text-center text-gray-500 text-xs mt-2 flex items-center justify-center group-hover:text-[#285BBB] transition-colors">
                    Centre agréé Cambridge
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </p>
                </a>
              ) : (
                <div className="block bg-white rounded-lg p-4">
                  <img 
                    src="/cambridge-assessment.png" 
                    alt="Cambridge Assessment" 
                    className="h-14 w-auto mx-auto"
                  />
                  <p className="text-center text-gray-500 text-xs mt-2">
                    Centre agréé Cambridge
                  </p>
                </div>
              )}
              
              {/* Logo LEVELTEL */}
              <div className="bg-white rounded-lg p-3">
                <img 
                  src={leveltelLogoUrl} 
                  alt="LEVELTEL Certifiant" 
                  className="h-10 w-auto mx-auto"
                />
              </div>
              
              {/* Avis Google et Facebook */}
              <div className="grid grid-cols-2 gap-3">
                <a 
                  href={googleReviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 bg-white rounded-lg p-3 hover:shadow-lg transition-all"
                >
                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                  <span className="text-gray-700 text-xs font-medium">Avis Google</span>
                </a>
                <a 
                  href={facebookReviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 bg-white rounded-lg p-3 hover:shadow-lg transition-all"
                >
                  <Facebook className="h-4 w-4 text-[#1877F2]" />
                  <span className="text-gray-700 text-xs font-medium">Avis Facebook</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col space-y-4">
            {/* Ligne 1 : Copyright et liens légaux */}
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-xs sm:text-sm whitespace-nowrap">
                © {new Date().getFullYear()} The English Speaking Center. Tous droits réservés.
              </p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                {legalLinks.map((link, idx) => (
                  <Link 
                    key={idx}
                    to={link.path} 
                    className="text-gray-400 hover:text-white text-xs sm:text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Ligne 2 : Application développée par MSC + Version */}
            <div className="flex flex-col md:flex-row justify-between items-center pt-4 border-t border-white/5 space-y-2 md:space-y-0">
              <a 
                href="https://agence-msc.fr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors group"
              >
                <span className="text-gray-500 text-xs">Application développée par</span>
                <img 
                  src={MSC_LOGO_URL}
                  alt="Agence MSC"
                  className="h-6 w-auto opacity-70 group-hover:opacity-100 transition-opacity"
                />
              </a>
              <div className="flex items-center space-x-4 text-gray-500 text-xs" data-testid="footer-version">
                <span>Déployé le {deployedAt}</span>
                <span>|</span>
                <span>Version {appVersion}{build.git_commit ? ` (${build.git_commit})` : ''}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
